import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { schoolService, userService } from '../../../services';
import { buildTeacherCreatePayload } from '../utils/buildTeacherCreatePayload';
import {
  DEFAULT_ADMIN_LIST_SORT,
  normalizeAdminListSort,
} from '../../../lib/admin-list-sort';
import type { 
  SchoolTeacher, 
  SchoolTeacherFilters, 
  SchoolTeacherCreateRequest, 
  SchoolTeacherUpdateRequest,
  SchoolTeachersResponse,
  SchoolTeacherPagination
} from '../types';

// URL state parsers
const schoolIdParser = parseAsString.withDefault('');
const searchParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const sortParser = parseAsString.withDefault(DEFAULT_ADMIN_LIST_SORT);
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

// Type cho query key
type TeacherQueryKey = ['teachers', SchoolTeacherFilters, { page: number; per_page: number }];

const useTeachers = () => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [schoolId, setSchoolId] = useQueryState('schoolId', schoolIdParser);
  const [search, setSearch] = useQueryState('search', searchParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [sort, setSort] = useQueryState('sort', sortParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters: SchoolTeacherFilters = useMemo(() => {
    const filterObj = {
      schoolId: schoolId && schoolId !== '' ? schoolId : undefined,
      search: search && search !== '' ? search : undefined,
      status: status && status !== '' ? (status as 'active' | 'inactive' | 'on_leave') : undefined,
      sort: normalizeAdminListSort(sort),
    };
    console.log('useTeachers - Filters updated:', filterObj, { schoolId, search, status, sort });
    return filterObj;
  }, [schoolId, search, status, sort]);

  // Query key cho caching
  const queryKey: TeacherQueryKey = ['teachers', filters, { page, per_page }];
  console.log('useTeachers - Query key:', queryKey);

  // Fetch teachers với react-query
  const {
    data: teachersData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('useTeachers - Query triggered with:', { filters, page, per_page });
      
      // Use single API endpoint with filters
      const response = await schoolService.getSchoolTeachers({
        ...filters,
        page,
        per_page
      });
      
      // Use server pagination if available, otherwise create fallback
      let pagination;
      if (response.pagination) {
        pagination = {
          page: response.pagination.current_page || 1,
          per_page: response.pagination.per_page || 10,
          total: response.pagination.total || 0,
          totalPages: response.pagination.total_pages || 1
        };
      } else {
        pagination = {
          page: 1,
          per_page: response.data?.length || 0,
          total: response.data?.length || 0,
          totalPages: 1
        };
      }
      
      return {
        data: response.data || [],
        pagination
      };
    },
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Fetch single teacher
  const useTeacherDetail = (id: string) => {
    return useQuery({
      queryKey: ['teacher', id],
      queryFn: async () => {
        // For now, we'll need to find the teacher from the list
        // In a real implementation, you'd have a getSchoolTeacher method
        const response = await schoolService.getSchoolTeachers();
        return response.data.find((teacher: any) => teacher.id === id);
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });
  };

  // /schooluser/teachers tạo user không có user_name/mật khẩu nên đi qua POST /user
  const createTeacherMutation = useMutation({
    // Tạo tài khoản không gửi lại an toàn được (dễ tạo trùng), còn retry mặc định của app treo khi tab mất focus
    retry: false,
    mutationFn: (data: SchoolTeacherCreateRequest) => userService.createUser(buildTeacherCreatePayload(data)),
    onSuccess: () => {
      // Invalidate và refetch teachers list
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
    },
  });

  // Update teacher mutation
  const updateTeacherMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SchoolTeacherUpdateRequest }) => {
      // For now, we'll use a placeholder - you'll need to implement this in schoolService
      const response = await schoolService.updateSchoolTeacher(id, data);
      return response;
    },
    onSuccess: (updatedTeacher) => {
      // Update cache cho teacher detail
      queryClient.setQueryData(['teacher', updatedTeacher.id], updatedTeacher);
      // Invalidate teachers list
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
    },
  });

  // Delete teacher mutation
  const deleteTeacherMutation = useMutation({
    mutationFn: async (id: string) => {
      // For now, we'll use a placeholder - you'll need to implement this in schoolService
      const response = await schoolService.deleteSchoolTeacher(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
    },
  });

  // Toggle teacher status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: SchoolTeacher['status'] }) => {
      const response = await schoolService.toggleSchoolTeacherStatus(id, status);
      return response;
    },
    onSuccess: (updatedTeacher) => {
      queryClient.setQueryData(['teacher', updatedTeacher.id], updatedTeacher);
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<SchoolTeacherFilters>) => {
    console.log('updateFilters called with:', newFilters);
    if (newFilters.schoolId !== undefined) {
      console.log('Setting schoolId:', newFilters.schoolId);
      setSchoolId(newFilters.schoolId || '');
    }
    if (newFilters.search !== undefined) setSearch(newFilters.search || '');
    if (newFilters.status !== undefined) setStatus(newFilters.status || '');
    if (newFilters.sort !== undefined) setSort(normalizeAdminListSort(newFilters.sort));
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [setSchoolId, setSearch, setStatus, setSort, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSchoolId('');
    setSearch('');
    setStatus('');
    setSort(null);
    setPage(1);
  }, [setSchoolId, setSearch, setStatus, setSort, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof SchoolTeacherFilters) => {
    switch (filterKey) {
      case 'schoolId':
        setSchoolId('');
        break;
      case 'search':
        setSearch('');
        break;
      case 'status':
        setStatus('');
        break;
      case 'sort':
        setSort(null);
        break;
    }
    setPage(1);
  }, [setSchoolId, setSearch, setStatus, setSort, setPage]);

  // Parse pagination from backend response
  const pagination = useMemo(() => {
    if (!teachersData) return undefined;
    
    // Backend returns pagination in different formats depending on whether pagination is used
    if (teachersData.pagination) {
      // Laravel paginator format - cast to any to access Laravel properties
      const paginationData = teachersData.pagination as any;
      return {
        page: paginationData.current_page || paginationData.page || 1,
        per_page: paginationData.per_page || 10,
        total: paginationData.total || 0,
        totalPages: paginationData.last_page || paginationData.totalPages || 1,
        // Laravel paginator properties
        current_page: paginationData.current_page,
        last_page: paginationData.last_page,
        from: paginationData.from,
        to: paginationData.to,
        has_more_pages: paginationData.has_more_pages,
        links: paginationData.links,
      };
    }
    
    // If no pagination info, assume single page
    return {
      page: 1,
      per_page: teachersData.data?.length || 0,
      total: teachersData.data?.length || 0,
      totalPages: 1,
    };
  }, [teachersData]);

  return {
    // Data
    teachers: teachersData?.data || [],
    pagination,
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createTeacher: createTeacherMutation.mutate,
    updateTeacher: updateTeacherMutation.mutate,
    deleteTeacher: deleteTeacherMutation.mutate,
    toggleTeacherStatus: toggleStatusMutation.mutate,

    // Mutation states
    isCreating: createTeacherMutation.isPending,
    isUpdating: updateTeacherMutation.isPending,
    isDeleting: deleteTeacherMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createTeacherMutation,
    updateTeacherMutation,
    deleteTeacherMutation,
    toggleStatusMutation,

    // Additional hooks
    useTeacherDetail,
  };
};

export { useTeachers };
